/*
 * RS multi buy (snippets/rs-multi-buy.liquid): "Acquisto multiplo" in RS main product.
 * - The button opens / closes the grid with a slide down (CSS grid rows 0fr -> 1fr).
 * - + and − change each row's quantity (0 up to the stock when Shopify tracks it); total pieces
 *   and price are updated while typing.
 * - Add to cart: one request to cart/add.js with every row above 0, then the cart drawer opens
 *   with the new lines (same as Dawn's product form). Without a cart drawer: the cart page.
 *   Shopify's answer on an error (e.g. not enough stock) is shown above the button.
 */
if (!customElements.get('rs-multi-buy')) {
  customElements.define(
    'rs-multi-buy',
    class RsMultiBuy extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.rs-mb__toggle');
        this.panel = this.querySelector('.rs-mb__panel');
        this.form = this.querySelector('.rs-mb__form');
        this.inputs = [...this.querySelectorAll('.rs-mb__input:not([disabled])')];
        this.addButton = this.querySelector('.rs-mb__add');
        this.error = this.querySelector('.rs-mb__error');
        this.count = this.querySelector('[data-rs-mb-count]');
        this.sum = this.querySelector('[data-rs-mb-sum]');

        this.panel.inert = !this.classList.contains('is-open');
        this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));

        this.querySelectorAll('.rs-mb__step').forEach((button) =>
          button.addEventListener('click', () => {
            const input = button.parentElement.querySelector('.rs-mb__input');
            this.setQuantity(input, (parseInt(input.value, 10) || 0) + Number(button.dataset.step));
          })
        );
        this.inputs.forEach((input) => {
          input.addEventListener('input', () => this.update());
          input.addEventListener('change', () => this.setQuantity(input, parseInt(input.value, 10) || 0));
          input.addEventListener('focus', () => input.select());
        });
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.update();
      }

      setOpen(open) {
        this.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', open);
        this.panel.inert = !open;
      }

      setQuantity(input, value) {
        const max = parseInt(input.max, 10);
        input.value = Math.max(0, Math.min(Number.isNaN(max) ? value : max, value));
        this.update();
      }

      items() {
        return this.inputs
          .map((input) => ({ id: Number(input.dataset.variantId), quantity: parseInt(input.value, 10) || 0 }))
          .filter((item) => item.quantity > 0);
      }

      update() {
        let pieces = 0;
        let cents = 0;
        this.inputs.forEach((input) => {
          const quantity = Math.max(0, parseInt(input.value, 10) || 0);
          pieces += quantity;
          cents += quantity * Number(input.dataset.price);
          input.closest('.rs-mb__row').classList.toggle('has-quantity', quantity > 0);
        });
        if (this.count) this.count.textContent = pieces;
        if (this.sum) this.sum.textContent = this.formatMoney(cents);
        if (!this.sending) this.addButton.disabled = pieces === 0;
      }

      // Shopify's money format, e.g. "€{{amount_with_comma_separator}}".
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const number = (value, decimals, thousands, decimal) => {
          const [whole, part] = (value / 100).toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? decimal + part : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return number(cents, 0, ',', '.');
            case 'amount_with_comma_separator':
              return number(cents, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return number(cents, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return number(cents, 2, "'", '.');
            case 'amount_with_space_separator':
              return number(cents, 2, ' ', ',');
            default:
              return number(cents, 2, ',', '.');
          }
        });
      }

      showError(message) {
        this.error.textContent = message || '';
        this.error.hidden = !message;
      }

      async onSubmit(event) {
        event.preventDefault();
        const items = this.items();
        if (!items.length || this.sending) return;
        this.sending = true;
        this.showError('');
        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-busy', 'true');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(this.addButton);
        }

        try {
          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            this.showError(data.description || data.message || window.cartStrings?.error || 'Error');
            return;
          }
          if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rs-multi-buy', cartData: data });
          }
          // Like Dawn's product form: a cart that was empty hides its lines until "is-empty" goes.
          cart.classList.remove('is-empty');
          // Dawn's drawer reads the product id from the answer of a single add.
          cart.renderContents({ ...data, id: items[0].id });
          this.inputs.forEach((input) => (input.value = 0));
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.sending = false;
          this.addButton.removeAttribute('aria-busy');
          this.update();
        }
      }
    }
  );
}
